import { useMemo } from 'react';
import { useCheckPermission } from './useUserRoles';
import { useSubconsConsolidatedPermissions } from './useConsolidatedPermissionsAdapter';

// Hook to check base Subcons access
export const useSubconsAccess = () => {
  const consolidated = useSubconsConsolidatedPermissions();
  const subconsAccess = useCheckPermission('SUBCONS_ACCESS');
  const procurementManage = useCheckPermission('PROCUREMENT_MANAGE_CONTRACTORS');

  return useMemo(() => {
    // Use consolidated if enabled
    if (consolidated.enabled) {
      return {
        data: consolidated.canAccess || false,
        isLoading: consolidated.isLoading
      };
    }

    // Fallback to individual checks
    const hasSubconsAccess = subconsAccess.data === true;
    const hasProcurementManage = procurementManage.data === true;
    const isLoading = subconsAccess.isLoading || procurementManage.isLoading;
    
    return {
      data: hasSubconsAccess || hasProcurementManage,
      isLoading: isLoading
    };
  }, [
    consolidated.enabled, consolidated.canAccess, consolidated.isLoading,
    subconsAccess.data, subconsAccess.isLoading,
    procurementManage.data, procurementManage.isLoading
  ]);
};

// Hook to check view permissions
export const useSubconsViewPermissions = () => {
  const consolidated = useSubconsConsolidatedPermissions();
  const canViewSubcons = useCheckPermission('SUBCONS_VIEW');

  return useMemo(() => {
    // Use consolidated if enabled
    if (consolidated.enabled) {
      return {
        viewSubcons: consolidated.canView || false,
        isLoading: consolidated.isLoading
      };
    }

    // Fallback to individual check
    return {
      viewSubcons: canViewSubcons.data || false,
      isLoading: canViewSubcons.isLoading
    };
  }, [
    consolidated.enabled, consolidated.canView, consolidated.isLoading,
    canViewSubcons.data, canViewSubcons.isLoading
  ]);
};

// Hook to check edit permissions
export const useSubconsEditPermissions = () => {
  const consolidated = useSubconsConsolidatedPermissions();
  const canEditSubcons = useCheckPermission('SUBCONS_EDIT');

  return useMemo(() => {
    // Use consolidated if enabled
    if (consolidated.enabled) {
      return {
        editSubcons: consolidated.canEdit || false,
        isLoading: consolidated.isLoading
      };
    }

    // Fallback to individual check
    return {
      editSubcons: canEditSubcons.data || false,
      isLoading: canEditSubcons.isLoading
    };
  }, [
    consolidated.enabled, consolidated.canEdit, consolidated.isLoading,
    canEditSubcons.data, canEditSubcons.isLoading
  ]);
};

// Hook to check manage permissions
export const useSubconsManagePermissions = () => {
  const consolidated = useSubconsConsolidatedPermissions();
  const canManageSubcons = useCheckPermission('SUBCONS_MANAGE');
  const canManageContractors = useCheckPermission('PROCUREMENT_MANAGE_CONTRACTORS');

  return useMemo(() => {
    // Use consolidated if enabled
    if (consolidated.enabled) {
      return {
        manageSubcons: consolidated.canManage || consolidated.canManageContractors || false,
        isLoading: consolidated.isLoading
      };
    }

    // Fallback to individual checks
    return {
      manageSubcons: canManageSubcons.data || canManageContractors.data || false,
      isLoading: canManageSubcons.isLoading || canManageContractors.isLoading
    };
  }, [
    consolidated.enabled, consolidated.canManage, consolidated.canManageContractors, consolidated.isLoading,
    canManageSubcons.data, canManageSubcons.isLoading,
    canManageContractors.data, canManageContractors.isLoading
  ]);
};